<script setup lang="ts">
import getObjectFromUrlParams from '@core/utils/getObjectFromUrlParams'
import type { UnifiedDepositResponse, QuickDepositRequestData, CashierDepositDataObject, QuickDepositDataObject, CashierDepositResponseData, CashierDepositRequestData, EpayPspClientAttributes } from '@payments/types'
import { type PaymentServiceComponents, FailedDepositReason } from '@core/types'
import { getDepositAmountFromMinorUnit } from '@payments/utils/depositAmount'
import { ref, computed, onMounted, nextTick, definePageMeta, useState, useRoute, useDeposit, usePayments, useUserInfo, useHead, usePaymentsConfig, navigateTo, useThemeLogo, whenever, useI18n, useScript, useTracking, useNuxtApp } from '#imports'

definePageMeta({
    title: 'Redirect assistant',
    layout: false,
    hideOverlayContainer: true,
    hideIntercom: true,
})

useHead({
    meta: [
        { name: 'robots', content: 'noindex, nofollow' }
    ]
})

const route = useRoute()
const { trackEvent } = useTracking()
const { isLoggedIn } = useUserInfo()
const { t } = useI18n()
const { pendingPageRedirectUrl } = usePaymentsConfig()
const logo = useThemeLogo('menu')
const { isFirstDeposit, useRequestCache, usePaymentRequest, startDepositAttempt, reportDepositFailure } = useDeposit()
const { isPspEpay } = usePayments()
// Taken from the cached request, which is all a fresh document knows about the deposit
let attempt: ReturnType<typeof startDepositAttempt>

const depositToken = computed(() => route.query.token as string)

const { data: requestCacheData, error: requestCacheError } = useRequestCache(depositToken)

const errorMessage = useState<string | null>('redirectAssistant:errorMessage')

// '' keeps the query disabled (like undefined) while satisfying MaybeRefOrGetter<string>.
const url = ref<string>('')
const requestData = ref<CashierDepositDataObject | QuickDepositDataObject>()

const nuxtApp = useNuxtApp()

const { data: depositResponseData, error: depositError, refresh: refreshDepositRequest } = usePaymentRequest(url, requestData, { enabled: false })

// TODO START: Remove when deposits use token solely
function convertToTrueValue(value: string): string | null | boolean {
    if (!value || value === 'null' || value === 'undefined') return null
    if (value === 'true' || value === '1') return true
    if (value === 'false' || value === '0') return false

    return value
}

function convertObjectToTrueValues(obj: Record<string, any>) {
    return Object.entries(obj).reduce((acc, [key, value]) => {
        acc[key as string] = convertToTrueValue(value)

        return acc
    }, {} as Record<string, any>)
}

function consolidateAttributes(query: any): QuickDepositRequestData | CashierDepositRequestData {
    const result: Record<string, any> = { attributes: {} }

    Object.entries(query).forEach(([key, value]) => {
        const match = key.match(/^([^[]+)\[([^\]]+)\]$/)

        if (match) {
            // Extract the main object name and its nested key
            const objectKey = match[1]
            const nestedKey = match[2]

            if (!objectKey) return

            // Ensure the object exists in the result
            if (!result[objectKey]) {
                result[objectKey] = {}
            }

            if (!nestedKey) return

            // Assign the value to the correct nested key
            result[objectKey][nestedKey] = value

            return
        }

        // Regular keys
        result[key] = value
    })

    return result as QuickDepositRequestData | CashierDepositRequestData
}

async function constructObjectFromUrlParams(routeQuery: any) {
    url.value = routeQuery.url

    const attributes = routeQuery?.attributes ? JSON.parse(routeQuery.attributes) : {}

    delete routeQuery.url
    delete routeQuery.attributes

    const requestDataQuery = routeQuery
    const trueValueQuery = convertObjectToTrueValues({ attributes, ...requestDataQuery })
    const consolidatedQuery = consolidateAttributes(trueValueQuery) as CashierDepositDataObject

    if (consolidatedQuery?.attributes) {
        // Transform redirect urls to always use pending
        const pendingUrl = consolidatedQuery.attributes.pendingUrl?.replace('/payment_iq_payments/success_url', '/payments/pending')

        consolidatedQuery.attributes.successUrl = pendingUrl
        consolidatedQuery.attributes.cancelUrl = pendingUrl
        consolidatedQuery.attributes.failureUrl = pendingUrl
        consolidatedQuery.attributes.pendingUrl = pendingUrl
    }

    requestData.value = consolidatedQuery as CashierDepositDataObject | QuickDepositDataObject

    await nextTick()

    await navigateTo({ query: {} }, { replace: true })

    refreshDepositRequest()
}
// TODO END: Remove when deposits use token solely

async function handleEpayRedirect(responseData: PaymentServiceComponents['schemas']['QuickDepositResponse']) {
    await trackEvent({
        event_name: 'quick_deposit_redirect_assistant_entry',
        event_type: 'Load',
        meta_data: [{
            category: 'Deposit',
            isFirstDeposit: isFirstDeposit.value,
            hasPspClient: !!responseData.pspClient,
        }]
    })

    const pspClient = responseData.pspClient

    // Generated `attributes` is an opaque record; the ePay payload is structured at runtime.
    const pspAttributes = pspClient?.attributes as EpayPspClientAttributes | undefined

    if (!pspClient || !pspAttributes) {
        await trackEvent({
            event_name: 'quick_deposit_redirect_assistant_no_psp_client',
            event_type: 'Load',
            meta_data: [{
                category: 'Deposit',
                isFirstDeposit: isFirstDeposit.value,
                hasPspClient: !!pspClient,
                hasAttributes: !!pspClient?.attributes,
            }]
        })

        handleDepositResponse({
            success: false,
            errors: [{ msg: t('core.defaultErrorMessage') }] as any,
        })

        return
    }

    const epayRedirectUrl = pspAttributes.failureUrl
    const redirectUrlWithTxId = epayRedirectUrl.replace('${transaction.id}', responseData.providerTransactionId ?? '')
    const pendingParams = getObjectFromUrlParams(redirectUrlWithTxId)

    const epayScript = nuxtApp.runWithContext(() => useScript({
        src: pspClient.clientUrl!,
    }, {
        trigger: 'client',
    })) as ReturnType<typeof useScript>

    epayScript.onLoaded(() => {
        window.epay
            .setSessionId(pspAttributes.id)
            .setSessionKey(pspClient.key)
            .setCallbacks({
                clientReady: async () => {
                    await trackEvent({
                        event_name: 'quick_deposit_epay_client_ready',
                        event_type: 'Load',
                        meta_data: [{
                            category: 'Deposit',
                            isFirstDeposit: isFirstDeposit.value,
                            hasPaymentMethodId: !!pspAttributes.attributes?.paymentMethodId,
                        }]
                    })

                    window.epay.setPaymentOptions({
                        amount: Number(requestData.value!.amount),
                        store: true,
                    })

                    const paymentMethodId = pspAttributes.attributes.paymentMethodId

                    if (!paymentMethodId) {
                        await trackEvent({
                            event_name: 'quick_deposit_missing_payment_method_id',
                            event_type: 'Load',
                            meta_data: [
                                {
                                    category: 'Deposit',
                                    isFirstDeposit: isFirstDeposit.value,
                                }
                            ]
                        })

                        errorMessage.value = t('core.defaultErrorMessage')

                        await navigateTo({ name: 'payments-pending', params: { txId: 'null' }, query: pendingParams })

                        return
                    }

                    window.epay.createTransaction({
                        amount: Number(requestData.value!.amount),
                        paymentMethodId
                    })
                },
                error: async (error: any) => {
                    errorMessage.value = t('core.defaultErrorMessage')

                    reportDepositFailure(attempt, FailedDepositReason.psp_error, error)

                    const errorCode = error?.errorCode ?? 'ePayErrorCodeUndefined'

                    if (isFirstDeposit.value) {
                        await trackEvent({
                            event_name: 'signup_deposit_endpoint_error_epay_error',
                            event_type: 'Load',
                            meta_data: [
                                {
                                    category: 'Deposit',
                                    errorMessage: error?.message,
                                    errorCode: errorCode,
                                    errorObject: error,
                                },
                            ]
                        })
                    }
                    else {
                        await trackEvent({
                            event_name: 'quick_deposit_epay_error',
                            event_type: 'Load',
                            meta_data: [
                                {
                                    category: 'Deposit',
                                    errorMessage: error?.message,
                                    errorCode: errorCode,
                                    errorObject: error
                                },
                            ]
                        })
                    }

                    await navigateTo({ name: 'payments-pending', params: { txId: 'null' }, query: pendingParams })
                }
            })
            .init()
    })

    epayScript.onError(async () => {
        errorMessage.value = t('core.defaultErrorMessage')

        reportDepositFailure(attempt, FailedDepositReason.setup_failed)

        if (isFirstDeposit.value) {
            await trackEvent({
                event_name: 'signup_deposit_load_error_epay_script',
                event_type: 'Load',
                meta_data: [
                    { category: 'Deposit' }
                ]
            })
        }
        else {
            await trackEvent({
                event_name: 'quick_deposit_epay_script_load_error',
                event_type: 'Load',
                meta_data: [
                    { category: 'Deposit' }
                ]
            })
        }

        await navigateTo({ name: 'payments-pending', params: { txId: 'null' }, query: pendingParams })
    })
}

// Also accepts the synthetic error object built when a request never produced a response.
type DepositResponseInput = UnifiedDepositResponse | { success: boolean, errors?: (CashierDepositResponseData['errors']) }

async function handleDepositResponse(data: DepositResponseInput) {
    const quickDepositResponse = data as PaymentServiceComponents['schemas']['QuickDepositResponse']
    const firstDepositResponse = data as PaymentServiceComponents['schemas']['FirstDepositResponse']
    const cashierResponse = data as CashierDepositResponseData
    const txId = quickDepositResponse?.providerTransactionId ?? cashierResponse?.txId
    const redirectUrlWithTxId = (requestData.value?.attributes?.pendingUrl ?? requestData.value?.pendingUrl)?.replace('${ptx.id}', txId)
    const pendingParams = redirectUrlWithTxId ? getObjectFromUrlParams(redirectUrlWithTxId) : {}

    if (!data?.success) {
        const firstError = cashierResponse?.errors?.[0]
        const firstErrorMessage = typeof firstError === 'string' ? firstError : firstError?.msg

        errorMessage.value = firstErrorMessage ?? quickDepositResponse?.messages?.[0] ?? t('core.defaultErrorMessage')

        if (isFirstDeposit.value) {
            await trackEvent({
                event_name: 'signup_deposit_endpoint_error_deposit_response',
                event_type: 'Load',
                meta_data: [
                    {
                        category: 'Deposit',
                        errorMessage: errorMessage.value,
                    },
                ],
            })
        }

        await navigateTo({
            name: 'payments-pending',
            params: { txId: txId ?? 'null' },
            query: pendingParams
        })

        return
    }

    // Quick deposit serializes `scaRedirect` (lowercase, payments' own DTO); first deposit serializes
    // `SCARedirect` (uppercase, from the shared PspIntegrationContract). Read both so the SCA redirect
    // fires for either deposit type.
    const scaRedirect = cashierResponse?.redirectOutput
        ?? quickDepositResponse?.scaRedirect
        ?? firstDepositResponse?.SCARedirect

    if (scaRedirect?.url) {
        if (isFirstDeposit.value) {
            await trackEvent({
                event_name: 'signup_deposit_sca_redirect',
                event_type: 'Load',
                meta_data: [
                    { category: 'Deposit' }
                ],
            })
        }

        await navigateTo(scaRedirect.url, { external: true })

        return
    }

    await navigateTo({
        name: 'payments-pending',
        params: { txId },
        query: pendingParams
    })
}

if (import.meta.client) {
    whenever(depositResponseData, (response) => {
        if (!response) return

        const quickDepositResponse = response as PaymentServiceComponents['schemas']['QuickDepositResponse']

        if (quickDepositResponse.pspClient) {
            handleEpayRedirect(quickDepositResponse)
        }
        else {
            handleDepositResponse(response)
        }
    }, { immediate: true })

    whenever(depositError, async (errorValue) => {
        if (!errorValue) return

        const errorResponse = {
            success: false,
            errors: [{ msg: errorValue.message }] as any,
        }

        handleDepositResponse(errorResponse)
    }, { immediate: true })

    whenever(requestCacheData, async (cached) => {
        if (cached.psp && cached.paymentMethod) {
            const requestAmount = Number(cached.amount) || 0

            attempt = startDepositAttempt({
                psp: cached.psp,
                paymentMethod: cached.paymentMethod,
                accountId: cached.accountId,
                amount: isPspEpay(cached.psp) ? getDepositAmountFromMinorUnit(requestAmount) : requestAmount,
                brand: cached.brand,
            })
        }

        if ('accountId' in cached) {
            // Cached for the failure report only; the quick deposit endpoint never took them
            delete cached.psp
            delete cached.paymentMethod
        }

        // Bridge the typed request-cache DTO to the hand-rolled deposit-data objects (same payload, differing field types).
        const newVal = cached as unknown as CashierDepositDataObject | QuickDepositDataObject
        url.value = newVal.url
        requestData.value = newVal

        if (requestData.value.url) {
            // `url` is structurally required but must be stripped before send; widen to partial to delete.
            delete (requestData.value as Partial<CashierDepositDataObject | QuickDepositDataObject>).url
        }

        await nextTick()

        refreshDepositRequest()
    }, { immediate: true })

    whenever(requestCacheError, async () => {
        await navigateTo(pendingPageRedirectUrl?.deposit ?? t('pages.lobby'))
    }, { immediate: true })
}

onMounted(async () => {
    if (!isLoggedIn.value) {
        await navigateTo(pendingPageRedirectUrl?.deposit ?? t('pages.lobby'))

        return
    }

    // TODO Remove everything below when deposits use token solely
    if (route.query.token) return

    if (!route.query?.url) {
        await navigateTo(pendingPageRedirectUrl?.deposit ?? t('pages.lobby'))

        return
    }

    const routeQuery = { ...route.query }

    constructObjectFromUrlParams(routeQuery)
})
</script>

<template>
    <UIApp>
        <div class="redirect-assistant" data-test="redirect-assistant">
            <div>
                <div class="redirect-assistant__content">
                    <div>
                        <NuxtImg
                            v-if="logo?.src"
                            preset="local"
                            loading="lazy"
                            provider="cloudflare"
                            decoding="async"
                            :src="logo.src"
                            :quality="logo.quality"
                            height="40"
                            alt="Site logo"
                            class="redirect-assistant__logo"
                        />
                    </div>

                    <div>
                        <h1 data-test="redirect-assistant-title" class="redirect-assistant__title">
                            {{ t('payments.deposit.redirectAssistant.title') }}
                        </h1>

                        <UILoader
                            data-test="redirect-assistant-loader"
                            class="redirect-assistant__loader"
                            :inline="true"
                            :text="t('payments.deposit.redirectAssistant.loadingText')"
                        />

                        <p data-test="redirect-assistant-title" class="redirect-assistant__subtitle">
                            {{ t('payments.deposit.redirectAssistant.subtitle') }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </UIApp>
</template>

<style lang="scss" scoped>
:deep(.loader--inline) {
    margin-left: 0;
}

.redirect-assistant {
    text-align: center;
    width: 100%;
    height: 100vh;
    padding: 20px;

    &__content {
        margin-top: 20px;
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    &__title {
        margin-top: 50px;
        font-size: $font-size-lg;
        font-weight: $text-bold;
    }

    &__subtitle {
        margin-top: 50px;
        font-weight: $text-regular;
        font-size: $font-size-sm;
    }
}
</style>
